import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";
import { adaptRegionReport } from "src/features/reporting/adapters";
import StoreObservationTableCell from "../../components/StoreObservationTableCell/StoreObservationTableCell";
import IndividualObservationTableCell from "../../components/IndividualObservationTableCell/IndividualObservationTableCell";
import GreatConversationTableCell from "../../components/GreatConversationTableCell/GreatConversationTableCell";

interface RegionReportOptions {
	year?: number;
	formId: number;
	compliancy?: boolean;
	threshold?: boolean;
	observationType: IObservationType;
}

const columnHelper = createColumnHelper<any>();

// Enqueue an async export. Backend returns 200 empty; the worker
// generates the workbook and emails a download link to the user.
const requestRegionReportExport = async (
	form: number,
	type: "complete" | "compliant" | "threshold",
	year?: number
) => {
	await cmsClient.post("/observations/reports/region-by-month/", {
		form_id: form,
		year,
		include_ignored: true,
		type
	});
};

const fetchRegionReport = async (
	formId: number,
	observationType: IObservationType,
	year?: number
) => {
	const response = await cmsClient.get<IRegionReport[]>(
		"/observations/reports/region-by-month/",
		{
			params: {
				year,
				form: formId,
				includeIgnored: true
			}
		}
	);

	return response.data;
};

const useRegionReport = ({
	formId,
	compliancy,
	threshold,
	observationType,
	year
}: RegionReportOptions) => {
	const queryKey = ["region-report", { formId, year }];
	const queryFn = () => fetchRegionReport(formId, observationType, year);

	const query = useQuery({ queryKey, queryFn, select: adaptRegionReport });

	const months = Array.from({ length: 12 }, (_, i) => i).map((month) =>
		dayjs().month(month).format("MMM")
	);

	const monthlyColumns = months.map((month) => {
		return columnHelper.accessor(month, {
			cell: ({ cell, row }) => {
				if (observationType === "INDIVIDUAL") {
					return <IndividualObservationTableCell cellValue={cell.getValue()} />;
				}

				if (observationType === "GREAT_CONVERSATION") {
					return <GreatConversationTableCell cellValue={cell.getValue()} />;
				}

				return (
					<StoreObservationTableCell
						cellValue={cell.getValue()}
						showStatistics={true}
						regionId={row.original.regionId}
					/>
				);
			},
			header: month
		});
	});

	const columns = [
		columnHelper.display({
			id: "region-name",
			cell: ({ row }) => row.original.name,
			header: "Region"
		}),
		...monthlyColumns
	];

	const download = async () => {
		try {
			await requestRegionReportExport(
				formId,
				compliancy ? "compliant" : threshold ? "threshold" : "complete",
				year
			);
			toast.success(
				<CustomToast
					type="Success"
					message="A link to your export (Region by month) has been generated. Check your mail inbox for the link."
				/>,
				{ hideProgressBar: true }
			);
		} catch (error) {
			toast.error(
				<CustomToast type="Error" message="Something went wrong, try again" />,
				{ hideProgressBar: true }
			);
		}
	};

	return { columns, download, ...query };
};

export default useRegionReport;
